import { parsePullRequestUrl, projectPullRequestRepository } from "@afar-research/afar";

export { projectPullRequestRepository };

/**
 * What a user typed into "Link pull request", resolved to a target the Environment can act on.
 *
 * A bare number means the thread's own project repository. A URL may name another repository,
 * which the owning Environment reads with its own credentials — the URL is a locator, never a
 * grant, so nothing here decides whether the target is readable.
 */

export interface LinkPullRequestTarget {
  readonly host: string;
  readonly repository: string;
  readonly number: number;
  readonly url: string;
}

export type LinkPullRequestParse =
  | { readonly kind: "target"; readonly target: LinkPullRequestTarget }
  /** A number with no project repository to resolve it against. */
  | { readonly kind: "needs-repository"; readonly number: number }
  | { readonly kind: "empty" }
  | { readonly kind: "unrecognized" };

const PULL_REQUEST_NUMBER = /^#?(?<number>\d+)$/u;
const GITHUB_CLI_CHECKOUT = /^gh\s+pr\s+checkout\s+(?<reference>.+)$/iu;

function positiveInteger(value: string | undefined): number | null {
  if (value === undefined) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export function parseLinkPullRequestInput(
  input: string,
  projectRepository: { readonly host: string; readonly repository: string } | null,
): LinkPullRequestParse {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { kind: "empty" };
  // `gh pr checkout <reference>` is what a user copies out of a terminal, so accept it as the
  // reference it wraps rather than making them edit it down.
  const reference = GITHUB_CLI_CHECKOUT.exec(trimmed)?.groups?.["reference"]?.trim() ?? trimmed;
  if (reference.length === 0) return { kind: "empty" };

  // One reading of a pull request address for the whole app: a surface that parsed it its own
  // way would store a membership another surface then fails to find.
  const url = parsePullRequestUrl(reference);
  if (url !== null) {
    return {
      kind: "target",
      // The locator is the one the user gave rather than one rebuilt from parts, so the link
      // opens the page they meant even where a host serves more than one path to it.
      target: { ...url, url: reference },
    };
  }

  const number = positiveInteger(PULL_REQUEST_NUMBER.exec(reference)?.groups?.["number"]);
  if (number === null) return { kind: "unrecognized" };
  if (projectRepository === null) return { kind: "needs-repository", number };
  return {
    kind: "target",
    target: {
      host: projectRepository.host.toLowerCase(),
      repository: projectRepository.repository.toLowerCase(),
      number,
      url: `https://${projectRepository.host}/${projectRepository.repository}/pull/${number}`,
    },
  };
}
